// Адрес сервера сети — рантайм-значение, а не константа сборки (Фаза 4).
//
// Одна сборка приложения работает с сервером любого клиента: адрес вводит оператор
// на экране `modules/setup`. Где он хранится — зависит от оболочки:
//   - Android (Capacitor): полный базовый URL в `@capacitor/preferences`, свойство
//     устройства; `getApiBaseUrl` его и отдаёт;
//   - Electron: origin бэкенда в `config.json` оболочки и в цели её прокси
//     (`desktop/src/apiTarget.ts`); веб-слой ходит на относительный `/api/v1`,
//     а адрес меняет через мост `window.restomarkerShell`;
//   - браузер / web/nginx-образ: `env.apiBaseUrl` из сборки (запасной).
//
// По образцу `shared/auth/tokenStorage` и `shared/storage/viewMode`: синхронная
// копия в памяти + `restore*()` из bootstrap до первого рендера. httpClient читает
// базовый URL СИНХРОННО на каждый запрос — асинхронное чтение отправило бы первый
// запрос в никуда.

import { Preferences } from '@capacitor/preferences';

import { env } from '@/shared/lib/env';
import { desktopShellBridge, isDesktopShell, type ApiTargetInfo } from '@/shared/lib/shell';
import { PREFERENCE_KEYS } from '@/shared/storage';

const KEY = PREFERENCE_KEYS.serverAddress;

// Уже нормализованное значение либо null. httpClient читает через getApiBaseUrl.
let cached: string | null = null;

// Electron: адрес бэкенда живёт в оболочке (config.json + мутабельная цель прокси),
// не в Preferences. Заполняется в restoreServerAddress через мост.
let shellInfo: ApiTargetInfo | null = null;

/**
 * Привести ввод оператора к базовому URL API.
 *
 * Оператор вводит либо origin (`https://cloud.example.ru`), либо полный базовый
 * адрес — с версией и слэшем или без. Хранится всегда полный базовый URL без
 * хвостового слэша: `buildUrl` в httpClient дописывает к нему путь ручки как есть,
 * и лишний `/api/v1` в пути дал бы `/api/v1/api/v1/...` (регрессия сторожится
 * тестом `modules/catalog/__tests__/api.test.ts`).
 */
export function normalizeServerAddress(raw: string): string {
  let value = raw.trim();
  if (value === '') {
    return '';
  }
  // Схема обязательна для fetch. Не указана — по умолчанию https; для LAN-стенда
  // по http оператор пишет схему сам (плейсхолдер поля — `https://…`).
  if (!/^https?:\/\//i.test(value)) {
    value = `https://${value}`;
  }
  value = value.replace(/\/+$/, '');
  // Версия уже в адресе (`.../api/v1`, `.../api/v2`) — не дублируем.
  if (!/\/api\/v\d+$/.test(value)) {
    value = `${value}/api/v1`;
  }
  return value;
}

/** Прочитать сохранённый адрес при старте. Вызывается из bootstrap до первого рендера. */
export async function restoreServerAddress(): Promise<void> {
  cached = null;
  shellInfo = null;

  const bridge = desktopShellBridge();
  if (bridge) {
    try {
      shellInfo = await bridge.getApiTarget();
    } catch {
      shellInfo = null;
    }
    return;
  }
  const { value } = await Preferences.get({ key: KEY });
  cached = value ? normalizeServerAddress(value) : null;
}

/**
 * Базовый URL API — ЕДИНСТВЕННЫЙ источник для `buildUrl` в httpClient.
 *
 * Пустая строка означает «адрес не задан»: гейт роутинга (`rootRoute.beforeLoad`)
 * уводит на экран настройки.
 */
export function getApiBaseUrl(): string {
  // В оболочке всё через локальный прокси, куда бы он ни целился.
  if (isDesktopShell()) {
    return '/api/v1';
  }
  if (cached) {
    return cached;
  }
  if (env.apiBaseUrl) {
    return env.apiBaseUrl;
  }
  return '';
}

/**
 * Есть ли рабочий адрес сервера. Читает гейт роутинга.
 *
 * В оболочке прокси есть всегда, но целиться он может в дефолт dev-стенда —
 * тогда гейт всё равно ведёт на `/setup`, пока оператор не подтвердит адрес.
 */
export function hasServerAddress(): boolean {
  if (isDesktopShell()) {
    return shellInfo?.explicit ?? false;
  }
  return getApiBaseUrl() !== '';
}

/**
 * Адрес, который надо показать на экране `/setup` как текущий: в оболочке — цель
 * прокси, иначе — сохранённый оператором. `null` — оператор ещё ничего не вводил.
 */
export function getStoredServerAddress(): string | null {
  if (isDesktopShell()) {
    return shellInfo?.explicit ? shellInfo.target : null;
  }
  return cached;
}

/**
 * Отметить в памяти, что цель прокси оболочки задана оператором.
 *
 * Оболочка (главный процесс) уже переключила прокси и записала `config.json` —
 * это лишь синхронизирует копию в рендерере, чтобы гейт роутинга пропустил дальше
 * без перезагрузки. Запись адреса тут ни при чём: базовый URL в оболочке всегда
 * относительный `/api/v1`.
 */
export function markShellTargetExplicit(origin: string): void {
  shellInfo = { target: origin, explicit: true };
}

/**
 * Сохранить адрес, введённый оператором.
 *
 * Сброс сессии и кэша при СМЕНЕ адреса делает вызывающий (`SetupPage`): этот модуль —
 * чистое хранилище, как `viewMode`, и не должен тянуть за собой auth и очистку
 * Preferences.
 */
export async function setServerAddress(raw: string): Promise<void> {
  const next = normalizeServerAddress(raw);
  cached = next || null;
  if (next) {
    await Preferences.set({ key: KEY, value: next });
  } else {
    await Preferences.remove({ key: KEY });
  }
}
